import api from "@/api";

// LSP-20532: the `project-share` list route is scoped to the project's OWNING organization (tenant
// isolation), so a signed-in external collaborator — a user from another organization who reached the
// project through a share — gets 404 "Sorry, we couldn't find that project." from it every time. The
// comments box and the @mention picker load their people roster from that route and used to log the
// 404 as an error (and, in one caller, let it escape as an uncaught rejection). For those callers a
// 404 simply means "no roster is available to this viewer", so resolve to an empty roster instead.

export interface ProjectShareRosterRow {
	id?: string;
	projectId?: string;
	accessType?: string;
	User?: unknown;
	[key: string]: unknown;
}

export interface ProjectShareRoster {
	rows: ProjectShareRosterRow[];
	count: number;
}

const EMPTY_ROSTER: ProjectShareRoster = { rows: [], count: 0 };

export function isProjectNotFoundError(error: unknown): boolean {
	if (!error || typeof error !== "object") return false;
	const { httpStatus, code } = error as { httpStatus?: unknown; code?: unknown };
	return httpStatus === 404 || (httpStatus === undefined && code === 404);
}

// True when the signed-in viewer's organization owns the project. Fails OPEN (true) when either id
// is unknown so a same-org viewer is never denied the roster before the ids have loaded; the server
// remains the authority and a 404 from it is tolerated by loadProjectShareRoster below.
export function viewerOrganizationOwnsProject(viewerOrganizationId?: string | null, projectOrganizationId?: string | null): boolean {
	if (!viewerOrganizationId || !projectOrganizationId) return true;
	return viewerOrganizationId === projectOrganizationId;
}

export async function loadProjectShareRoster(
	projectId: string,
	options: { pageSize?: number; where?: Record<string, unknown> } = {}
): Promise<ProjectShareRoster> {
	try {
		const result = await api.getProjectShares({
			...options,
			where: { ...(options.where ?? {}), projectId }
		});
		return { rows: result?.rows ?? [], count: result?.count ?? result?.rows?.length ?? 0 };
	} catch (error) {
		if (isProjectNotFoundError(error)) {
			console.info(
				"[ProjectShares] Roster unavailable to this viewer (project is owned by another organization); using an empty roster",
				{
					projectId
				}
			);
			return { ...EMPTY_ROSTER, rows: [] };
		}
		throw error;
	}
}
